/**
 * PWA install affordance.
 *
 * Three paths, in decreasing order of slickness:
 *
 *  1. Native prompt. Chrome / Edge / Android-Chrome fire
 *     `beforeinstallprompt` when the site is installable. We stash the
 *     event and show "Install Gamez" — one click, native dialog.
 *
 *  2. Manual hint on Android/desktop Chromium if the event hasn't
 *     fired yet (e.g. Chrome's engagement heuristic hasn't unlocked
 *     the prompt, or the user recently uninstalled and the cooldown
 *     is active). Clicking opens a small dialog with the menu path.
 *
 *  3. iOS Safari: the `beforeinstallprompt` API doesn't exist at all.
 *     We detect iOS-Safari-in-a-regular-tab (no `standalone` mode)
 *     and show instructions for the Share → Add to Home Screen flow.
 *
 * When the app is already running standalone (installed and launched
 * from the home-screen icon) or when `appinstalled` fires, the whole
 * component renders nothing.
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // jsdom under vitest has no matchMedia — guard so tests don't throw.
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  }
  // iOS Safari sets navigator.standalone when launched from home screen.
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  // iPad on iOS 13+ reports Mac but has touch.
  const iOSUa = /iPad|iPhone|iPod/.test(ua);
  const iPadLike =
    ua.includes('Macintosh') &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  if (!(iOSUa || iPadLike)) return false;
  // Safari's UA doesn't include "Chrome" or "CriOS" — other iOS browsers do.
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | undefined>();
  const [installed, setInstalled] = useState(false);
  const [showingManual, setShowingManual] = useState(false);

  useEffect(() => {
    function onBeforeInstall(event: Event) {
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setInstalled(true);
      setPrompt(undefined);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Running as an installed PWA → nothing to offer.
  if (isStandalone()) return null;
  if (installed) return <p data-testid="install-installed">installed</p>;

  async function handleInstall() {
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstalled(true);
    }
    setPrompt(undefined);
  }

  // Native prompt ready — one-click path.
  if (prompt) {
    return (
      <button
        data-testid="install-button"
        type="button"
        className="primary install-btn"
        onClick={() => {
          void handleInstall();
        }}
      >
        📲 Install Gamez
      </button>
    );
  }

  // No native prompt. Show a hint button if we can reasonably assume the
  // user is on a platform that supports installs.
  const canShowManual = isIosSafari() || /Chrome|CriOS|EdgA|EdgiOS/.test(navigator.userAgent);
  if (!canShowManual) return null;

  const iosFlow = isIosSafari();
  return (
    <>
      <button
        data-testid="install-hint"
        type="button"
        className="ghost install-btn"
        onClick={() => {
          setShowingManual((v) => !v);
        }}
      >
        📲 Install
      </button>
      {showingManual && (
        <div className="install-hint-popover" role="dialog" aria-label="install instructions">
          {iosFlow ? (
            <>
              <strong>Install on iOS</strong>
              <ol>
                <li>
                  Tap the Share button <span aria-hidden="true">⬆️</span> in Safari's toolbar.
                </li>
                <li>
                  Scroll down and tap <em>Add to Home Screen</em>.
                </li>
                <li>Tap Add in the top-right corner.</li>
              </ol>
            </>
          ) : (
            <>
              <strong>Install on Android / Chrome</strong>
              <ol>
                <li>
                  Open the browser menu <span aria-hidden="true">⋮</span>.
                </li>
                <li>
                  Tap <em>Install app</em> (or <em>Add to Home screen</em>).
                </li>
              </ol>
              <p className="faint">
                If the option doesn't appear, Chrome may still be on a post-uninstall cooldown — try
                again in a few minutes or keep using the site; the prompt will re-appear.
              </p>
            </>
          )}
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setShowingManual(false);
            }}
          >
            close
          </button>
        </div>
      )}
    </>
  );
}
